/**
 * Inicio de DEENEX Activa (DISENO §1): lo que la pantalla dice de la base en uso, en palabras. Puro y testeado
 * (`inicio.test.ts`).
 */

import { num } from "../../campana-nueva/utils/format";
import { R_SUBIR_BASE, diaMes } from "../../base-ventas/logic/base";
import { R_PUBLICO } from "../../publico/logic/publico";
import type { EstadoBase } from "../../base-ventas/types";

export const R_INICIO = "/activa/inicio";

/**
 * «Hola, HATSU». Si el nombre del comercio es un id (no se guardó el nombre, `nombreActual` cae al brandId) o no tiene
 * letras, «Hola» a secas: la misma regla que las iniciales del menú (`iniciales`, Sidebar.tsx).
 */
export function saludo(nombre: string | null | undefined): string {
  const n = String(nombre ?? "").trim();
  if (!n || /^[0-9a-f]{24}$/i.test(n) || !/\p{L}/u.test(n)) return "Hola";
  return `Hola, ${n}`;
}

/** «Hoy», «Ayer» o «Hace 21 días» (los días los cuenta el servidor, en la hora del comercio). */
export function haceDias(dias: number): string {
  if (dias <= 0) return "Hoy";
  if (dias === 1) return "Ayer";
  return `Hace ${num(dias)} días`;
}

export type Tono = "rojo" | "ambar" | "neutro";

/**
 * El paso 01 de «Así funciona» («Tu base»): qué dice de la base en uso y a dónde lleva. Sin base, a subirla; con base,
 * a Público, donde están su antigüedad, el historial y «Subir base de datos».
 */
export function pasoTuBase(estado: EstadoBase): { texto: string; tono: Tono; destino: string; vieja: boolean } {
  if (!estado.enUso) return { texto: "Sin subir", tono: "ambar", destino: R_SUBIR_BASE, vieja: false };
  if (estado.dias === null) return { texto: "Sin ventas con fecha", tono: "ambar", destino: R_PUBLICO, vieja: false };
  return { texto: haceDias(estado.dias), tono: estado.vieja ? "rojo" : "neutro", destino: R_PUBLICO, vieja: estado.vieja };
}

/** El título del aviso de base vieja: «Tu base es del 21/09.», con la última venta que trae (duda 7 de la fase 0). */
export function tituloAvisoBase(estado: EstadoBase): string {
  const dia = diaMes(estado.enUso?.hasta);
  return dia ? `Tu base es del ${dia}.` : "Tu base está desactualizada.";
}

const R_CONFIGURACION_OFICIAL = "/activa/oficial/configuracion";
const R_DESCUENTOS = "/activa/descuentos";
const R_BRANDING = "/activa/branding";

/** Lo que falta para que una campaña salga como tiene que salir: «Para revisar» en Inicio. */
export interface AvisoDeInicio {
  id: "base" | "whatsapp" | "descuentos" | "branding";
  titulo: string;
  texto: string;
  accion: string;
  destino: string;
}

/**
 * Los avisos de Inicio, del más importante al menos. Un dato que no se pudo leer (`null`) no avisa nada: Inicio no
 * afirma que falta algo que no sabe. La base vieja tiene su aviso propio, arriba de todo.
 */
export function avisosDeInicio(o: { hayBase: boolean | null; whatsapp: boolean | null; descuentos: boolean | null; branding: boolean | null }): AvisoDeInicio[] {
  const out: AvisoDeInicio[] = [];
  if (o.hayBase === false) {
    out.push({ id: "base", titulo: "Subí tu base de ventas", texto: "Es el punto de partida: con ella DEENEX arma los grupos de tu primera campaña.", accion: "Subir base", destino: R_SUBIR_BASE });
  }
  if (o.whatsapp === false) {
    out.push({ id: "whatsapp", titulo: "Tus campañas salen sólo por mail", texto: "Sin WhatsApp conectado, el agente pone todos los grupos por mail. Conectá el número oficial o una línea por QR.", accion: "Conectar WhatsApp", destino: R_CONFIGURACION_OFICIAL });
  }
  if (o.descuentos === false) {
    out.push({ id: "descuentos", titulo: "Cargá tus descuentos", texto: "Sin un tope, el agente no sabe hasta cuánto puede ofrecer.", accion: "Cargar descuentos", destino: R_DESCUENTOS });
  }
  if (o.branding === false) {
    out.push({ id: "branding", titulo: "Cargá tu marca", texto: "Hasta que la guardes, tus mails y el reporte salen con el diseño de DEENEX.", accion: "Cargar tu marca", destino: R_BRANDING });
  }
  return out;
}
